import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Fechamentos_StoreInput';
import '../contracts/Fechamentos_UpdateInput';
import '../contracts/Fechamentos_ReabrirInput';
import '../contracts/Fechamentos_RecalcularInput';
import '../contracts/Fechamentos_CalcularLucroInput';
import '../contracts/Fechamentos_AcumularDadosInput';
import '../contracts/Fechamentos_AbertoResponse';
import '../contracts/Fechamentos_AcumularDadosResponse';
import '../contracts/Fechamentos_CalcularLucroResponse';
import '../contracts/Fechamentos_DestroyResponse';
import '../contracts/Fechamentos_FecharResponse';
import '../contracts/Fechamentos_IndexResponse';
import '../contracts/Fechamentos_ReabrirResponse';
import '../contracts/Fechamentos_RecalcularResponse';
import '../contracts/Fechamentos_ResultadoFinanceiroResponse';
import '../contracts/Fechamentos_ResumoResponse';
import '../contracts/Fechamentos_ShowResponse';
import '../contracts/Fechamentos_StoreResponse';
import '../contracts/Fechamentos_UpdateResponse';

import type { Fechamentos_StoreInput } from '../contracts/Fechamentos_StoreInput';
import type { Fechamentos_UpdateInput } from '../contracts/Fechamentos_UpdateInput';
import type { Fechamentos_RecalcularInput } from '../contracts/Fechamentos_RecalcularInput';
/**
 * Composable for Fechamentos Mensais (Monthly Accounting Periods) operations
 * Provides methods to interact with the Fechamentos API
 *
 * TYPED DATA LAYER: Date handling follows the typed data layer pattern:
 * - DATE-ONLY fields: Use fromDateToApiDate (YYYY-MM-DD, no timezone conversion)
 * - DATETIME fields: Use toApiDateTimeFormat/fromApiDateTimeFormat (ISO 8601 UTC)
 */
export function useFechamentos() {
  const isLoading = ref(false);
  const error = ref(null);

  // Initialize date formatter for typed data layer conversions
  const { fromDateToApiDate } = useDateFormatter();

  /**
   * Fetch all fechamentos with optional filters
   * @param {Object} filters - Filter options
   * @param {number} filters.franquia_id - Filter by franchise
   * @param {string} filters.status - Filter by status (aberto, fechado)
   * @param {number} page - Page number
   * @param {number} perPage - Items per page
   */
  const fetchFechamentos = async (filters: any = {}, page = 1, perPage = 20) => {
    log('useFechamentos', 'fetchFechamentos', 'Fetching fechamentos', { filters, page, perPage });
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if ((filters as any).franquia_id) params.append('franquia_id', (filters as any).franquia_id);
      if ((filters as any).status) params.append('status', (filters as any).status);
      if ((filters as any).data_inicial) params.append('data_inicial', (filters as any).data_inicial);
      if ((filters as any).data_final) params.append('data_final', (filters as any).data_final);
      params.append('page', String(page));
      params.append('per_page', String(perPage));

      const timestamp = new Date().getTime();
      const response = await httpService.get(`/index.php/fechamentos?${params.toString()}&_t=${timestamp}`, {
        responseContract: 'Fechamentos_IndexResponse',
      });
      log('useFechamentos', 'fetchFechamentos', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar fechamentos');
      }
    } catch (err) {
      log('useFechamentos', 'fetchFechamentos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single fechamento by ID
   * @param {number} id - Fechamento ID
   */
  const fetchFechamento = async (id: any) => {
    log('useFechamentos', 'fetchFechamento', 'Fetching fechamento', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/fechamentos/show/${id}`, {
        responseContract: 'Fechamentos_ShowResponse'
      });
      log('useFechamentos', 'fetchFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar fechamento');
      }
    } catch (err) {
      log('useFechamentos', 'fetchFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get the current open fechamento for a franchise
   * @param {number} franquiaId - Franchise ID
   */
  const fetchFechamentoAberto = async (franquiaId: number) => {
    log('useFechamentos', 'fetchFechamentoAberto', 'Fetching open fechamento', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/fechamentos/aberto/${franquiaId}`, {
        responseContract: 'Fechamentos_AbertoResponse'
      });
      log('useFechamentos', 'fetchFechamentoAberto', 'Response', response);

      if (response.success) {
        // Backend returns an empty object (all null fields) when no open fechamento exists
        const dados = response.dados as Record<string, unknown> | null;
        if (!dados || !dados.id) {
          return null;
        }
        return dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar fechamento aberto');
      }
    } catch (err) {
      log('useFechamentos', 'fetchFechamentoAberto', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new fechamento
   * @param {Object} data - Fechamento data
   */
  const createFechamento = async (data: Fechamentos_StoreInput) => {
    log('useFechamentos', 'createFechamento', 'Creating fechamento', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Fechamentos_StoreInput', data);
      const response = await httpService.post('/index.php/fechamentos/store', data, {
        responseContract: 'Fechamentos_StoreResponse',
        inputContract: 'Fechamentos_StoreInput',
      });
      log('useFechamentos', 'createFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar fechamento');
      }
    } catch (err) {
      log('useFechamentos', 'createFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update a fechamento (metas only)
   * @param {number} id - Fechamento ID
   * @param {Object} data - Update data (meta_faturamento, meta_clientes)
   */
  const updateFechamento = async (id: any, data: Fechamentos_UpdateInput) => {
    log('useFechamentos', 'updateFechamento', 'Updating fechamento', { id, data });
    isLoading.value = true;
    error.value = null;

    try {
      validate('Fechamentos_UpdateInput', data);
      const response = await httpService.put(`/index.php/fechamentos/update/${id}`, data, {
        responseContract: 'Fechamentos_UpdateResponse',
        inputContract: 'Fechamentos_UpdateInput',
      });
      log('useFechamentos', 'updateFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar fechamento');
      }
    } catch (err) {
      log('useFechamentos', 'updateFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a fechamento (soft delete)
   * @param {number} id - Fechamento ID
   */
  const deleteFechamento = async (id: any) => {
    log('useFechamentos', 'deleteFechamento', 'Deleting fechamento', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete(`/index.php/fechamentos/destroy/${id}`, {
        responseContract: 'Fechamentos_DestroyResponse'
      });
      log('useFechamentos', 'deleteFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao excluir fechamento');
      }
    } catch (err) {
      log('useFechamentos', 'deleteFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Close a fechamento period
   * @param {number} id - Fechamento ID
   */
  const fecharFechamento = async (id: number | string) => {
    log('useFechamentos', 'fecharFechamento', 'Closing fechamento', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/fechamentos/fechar/${id}`, null, {
        responseContract: 'Fechamentos_FecharResponse',
        inputContract: 'Fechamentos_FecharInput'
      });
      log('useFechamentos', 'fecharFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao fechar período');
      }
    } catch (err) {
      log('useFechamentos', 'fecharFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reopen a closed fechamento period
   * @param {number} id - Fechamento ID
   * @param {string} motivo - Reason for reopening
   */
  const reabrirFechamento = async (id: number, motivo: string) => {
    log('useFechamentos', 'reabrirFechamento', 'Reopening fechamento', { id, motivo });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/fechamentos/reabrir/${id}`, { motivo }, {
        responseContract: 'Fechamentos_ReabrirResponse',
        inputContract: 'Fechamentos_ReabrirInput',
      });
      log('useFechamentos', 'reabrirFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao reabrir período');
      }
    } catch (err) {
      log('useFechamentos', 'reabrirFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get summary statistics for a fechamento
   * @param {number} id - Fechamento ID
   */
  const fetchResumo = async (id: any) => {
    log('useFechamentos', 'fetchResumo', 'Fetching resumo', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/fechamentos/resumo/${id}`, {
        responseContract: 'Fechamentos_ResumoResponse',
      });
      log('useFechamentos', 'fetchResumo', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar resumo');
      }
    } catch (err) {
      log('useFechamentos', 'fetchResumo', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Recalculate valor_realizado for a fechamento
   * @param {number} id - Fechamento ID
   */
  const recalcularFechamento = async (id: any) => {
    log('useFechamentos', 'recalcularFechamento', 'Recalculating', id);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Fechamentos_RecalcularInput = {};
      const response = await httpService.post(`/index.php/fechamentos/recalcular/${id}`, payload, {
        responseContract: 'Fechamentos_RecalcularResponse',
        inputContract: 'Fechamentos_RecalcularInput',
      });
      log('useFechamentos', 'recalcularFechamento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao recalcular');
      }
    } catch (err) {
      log('useFechamentos', 'recalcularFechamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Acumula dados de OS, Recebimentos e Despesas no fechamento
   * @param {number} id - Fechamento ID
   */
  const acumularDados = async (id: number) => {
    log('useFechamentos', 'acumularDados', 'Accumulating data', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/fechamentos/acumular_dados/${id}`, null, {
        responseContract: 'Fechamentos_AcumularDadosResponse',
        inputContract: 'Fechamentos_AcumularDadosInput',
      });
      log('useFechamentos', 'acumularDados', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao acumular dados');
      }
    } catch (err) {
      log('useFechamentos', 'acumularDados', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Calcula o lucro mensal do fechamento
   * @param {number} id - Fechamento ID
   */
  const calcularLucro = async (id: number) => {
    log('useFechamentos', 'calcularLucro', 'Calculating profit', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/fechamentos/calcular_lucro/${id}`, null, {
        responseContract: 'Fechamentos_CalcularLucroResponse',
        inputContract: 'Fechamentos_CalcularLucroInput',
      });
      log('useFechamentos', 'calcularLucro', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao calcular lucro');
      }
    } catch (err) {
      log('useFechamentos', 'calcularLucro', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Retorna relatório completo de resultado financeiro
   * @param {number} id - Fechamento ID
   */
  const getResultadoFinanceiro = async (id: any) => {
    log('useFechamentos', 'getResultadoFinanceiro', 'Fetching financial result', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/fechamentos/resultado_financeiro/${id}`, {
        responseContract: 'Fechamentos_ResultadoFinanceiroResponse',
      });
      log('useFechamentos', 'getResultadoFinanceiro', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar resultado financeiro');
      }
    } catch (err) {
      log('useFechamentos', 'getResultadoFinanceiro', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchFechamentos,
    fetchFechamento,
    fetchFechamentoAberto,
    createFechamento,
    updateFechamento,
    deleteFechamento,
    fecharFechamento,
    reabrirFechamento,
    fetchResumo,
    recalcularFechamento,
    acumularDados,
    calcularLucro,
    getResultadoFinanceiro
  };
}
